import React, { useEffect, useState } from "react";
import { z } from "zod";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { FaUser, FaEnvelope, FaLock, FaEye, FaEyeSlash } from "react-icons/fa";
import SupportModal from "@/components/modals/SupportModal";
import GoogleSignInButton from "@/components/GoogleSignInButton";
import { failedToast } from "@/utils/toasts";

import { Link, useNavigate } from "react-router-dom";
import {
  useGetProfile,
  useSignin,
  useSignUp,
  useVerify2FA,
  useResend2FA,
  useResendVerificationEmail,
  useGoogleAuth,
} from "@/api/AuthApi";

import SmallLoader from "@/components/SmallLoader";
import { useAuthStore } from "@/stores/authStore";
import { LoginFormType } from "@/types/forms";
import { UserRoles } from "@/types/enums/actors-types";
import { handleApiErrors } from "@/utils/errorMapper";
import { COUNTRIES } from "@/constants/contries";
import { getPasswordStrength } from "@/utils/passwordStrength";

// Zod schemas for form validation
const loginSchema = z.object({
  username: z.string().min(1, "Le nom d'utilisateur ou l'email est requis"),
  password: z.string().min(1, "Le mot de passe est requis"),
});

const signupSchema = z
  .object({
    username: z
      .string()
      .min(3, "Le nom d'utilisateur doit comporter au moins 3 caractères"),
    first_name: z
      .string()
      .min(3, "Le prénom doit comporter au moins 3 caractères"),
    last_name: z.string().min(3, "Le nom doit comporter au moins 3 caractères"),
    email: z.string().email("Email invalide"),
    password: z
      .string()
      .min(8, "Le mot de passe doit comporter au moins 8 caractères"),
    confirm_password: z
      .string()
      .min(8, "Le mot de passe doit comporter au moins 8 caractères"),
    role: z.enum([UserRoles.MENTOR, UserRoles.JEUNEINNOVATEUR], {
      errorMap: () => ({
        message: "Veuillez sélectionner un type de profil",
      }),
    }),
    country: z.string().min(1, "Veuillez sélectionner votre pays"),
    phone_number: z
      .string()
      .regex(
        /^\+\d{7,15}$/,
        "Format invalide. Utilisez +[indicatif][numéro], ex: +237123456789",
      ),
    newsletter_subscription: z.boolean().default(false),
    terms_accepted: z.boolean().refine((val) => val === true, {
      message:
        "Vous devez accepter les Conditions Générales d'Utilisation pour continuer",
    }),
  })
  .refine((data) => data.password === data.confirm_password, {
    message: "Les mots de passe ne correspondent pas",
    path: ["confirm_password"],
  });

const twoFactorSchema = z.object({
  code: z.string().regex(/^\d{6}$/, "Le code doit comporter 6 chiffres"),
});

const verificationEmailSchema = z.object({
  email: z.string().email("Veuillez entrer une adresse email valide."),
});

type VerificationEmailFormData = z.infer<typeof verificationEmailSchema>;

type TwoFactorFormData = z.infer<typeof twoFactorSchema>;

type LoginFormData = z.infer<typeof loginSchema>;
type SignupFormData = z.infer<typeof signupSchema>;

const LoginSignupTab: React.FC = () => {
  const [activeTab, setActiveTab] = useState(0);
  const { signIn, loading: signInLoading } = useSignin();
  const { signUp, loading: signUpLoading } = useSignUp();
  const { getProfile, loading: getProfileLoading } = useGetProfile();
  const { user } = useAuthStore();

  const navigate = useNavigate();

  const {
    register: signupRegister,
    handleSubmit: handleSignupSubmit,
    setError: setSignupError,
    watch: signupWatch,
    formState: { errors: signupErrors },
  } = useForm<SignupFormData>({
    resolver: zodResolver(signupSchema),
  });

  const selectedRole = signupWatch("role");
  const termsAccepted = signupWatch("terms_accepted");
  const signupPasswordValue = signupWatch("password");
  const passwordStrength = getPasswordStrength(signupPasswordValue || "");

  const {
    register: loginRegister,
    handleSubmit: handleLoginSubmit,
    setError: setLoginError,
    formState: { errors: loginErrors },
  } = useForm<LoginFormData>({
    resolver: zodResolver(loginSchema),
  });

  const { verify2FA, loading: verify2FALoading } = useVerify2FA();
  const { resend2FA, loading: resend2FALoading } = useResend2FA();

  const [twoFactorChallenge, setTwoFactorChallenge] = useState<{
    verificationId: string;
    method: string;
    detail: string;
  } | null>(null);

  const {
    register: twoFactorRegister,
    handleSubmit: handleTwoFactorSubmit,
    setError: setTwoFactorError,
    formState: { errors: twoFactorErrors },
  } = useForm<TwoFactorFormData>({
    resolver: zodResolver(twoFactorSchema),
  });

  const { resendVerificationEmail, loading: resendVerificationLoading } =
    useResendVerificationEmail();

  const [needsVerification, setNeedsVerification] = useState(false);
  const [verificationEmail, setVerificationEmail] = useState("");
  const [verificationResendSent, setVerificationResendSent] = useState(false);
  const [pendingMentorRequest, setPendingMentorRequest] = useState(false);

  const {
    register: registerVerificationEmail,
    handleSubmit: handleVerificationEmailSubmit,
    formState: { errors: verificationEmailErrors },
  } = useForm<VerificationEmailFormData>({
    resolver: zodResolver(verificationEmailSchema),
  });

  const [showLoginPassword, setShowLoginPassword] = useState(false);
  const [showSignupPassword, setShowSignupPassword] = useState(false);
  const [showSignupConfirmPassword, setShowSignupConfirmPassword] =
    useState(false);

  const getRedirectPath = (role: string) => {
    switch (role) {
      case UserRoles.MEMBER:
        return "/jeuneInnovateur/tableauDeBord";
      case UserRoles.ADMIN:
        return "/flowAdministration/tableauDeBord";
      case UserRoles.INVESTISSEUR:
        return "/investisseur/tableauDeBord";
      case UserRoles.MENTOR:
        return "/mentor/tableauDeBord";
      case UserRoles.VISITEUR:
        return "/visiteur/tableauDeBord";
      default:
        return "/";
    }
  };

  const onLoginSubmit = async (data: LoginFormType) => {
    const result = await signIn(data);

    if (result.success) {
      const userInfo = await getProfile();
      if (userInfo) {
        navigate(getRedirectPath(userInfo.role || ""));
      }
    } else if (result.twoFactorRequired) {
      setTwoFactorChallenge({
        verificationId: result.verificationId,
        method: result.method,
        detail: result.detail,
      });
    } else if (result.error) {
      const isUnverified =
        result.error.response?.data?.message ===
        "This account has not been verified yet.";

      if (isUnverified) {
        setNeedsVerification(true);
        // We only know their email if they typed it as the username field —
        // if they logged in with a plain username instead, we don't have it.
        setVerificationEmail(data.username.includes("@") ? data.username : "");
      } else {
        handleApiErrors(result.error, setLoginError, "Identifiants invalides");
      }
    }
  };

  const onTwoFactorSubmit = async (data: TwoFactorFormData) => {
    if (!twoFactorChallenge) return;

    const result = await verify2FA(
      twoFactorChallenge.verificationId,
      data.code,
    );
    if (result.success) {
      const userInfo = await getProfile();
      if (userInfo) {
        navigate(getRedirectPath(userInfo.role || ""));
      }
    } else if (result.error) {
      handleApiErrors(result.error, setTwoFactorError, "Code invalide");
    }
  };

  const onResend2FA = async () => {
    if (!twoFactorChallenge) return;
    const result = await resend2FA(twoFactorChallenge.verificationId);
    if (result.success) {
      setTwoFactorChallenge({
        verificationId: result.verificationId,
        method: result.method,
        detail: result.detail,
      });
    }
  };

  const onSignupSubmit = async (data: SignupFormData) => {
    // terms_accepted is a local gate only — not part of the documented API,
    // don't send it to the backend.
    const { terms_accepted, ...payload } = data;
    void terms_accepted; // explicitly discarded, satisfies no-unused-vars
    const result = await signUp(payload);
    if (result.success) {
      // Backend requires email verification before login — don't attempt
      // auto-login, it will always fail. Show a "check your email" screen.
      setNeedsVerification(true);
      setVerificationEmail(data.email);
      setPendingMentorRequest(data.role === "mentor");
    } else if (result.error) {
      handleApiErrors(
        result.error,
        setSignupError,
        "Erreur lors de l'inscription",
      );
    }
  };

  const onResendVerification = async () => {
    if (!verificationEmail) return;
    const result = await resendVerificationEmail(verificationEmail);
    if (result.success) setVerificationResendSent(true);
  };

  const onVerificationEmailSubmit = async (data: VerificationEmailFormData) => {
    setVerificationEmail(data.email);
    const result = await resendVerificationEmail(data.email);
    if (result.success) setVerificationResendSent(true);
  };

  const { signInWithGoogle, loading: googleAuthLoading } = useGoogleAuth();

  const onGoogleCredential = async (idToken: string) => {
    const result = await signInWithGoogle(idToken);
    if (result.success) {
      const userInfo = await getProfile();
      if (userInfo) {
        navigate(getRedirectPath(userInfo.role || ""));
      }
    } else if (result.twoFactorRequired) {
      setTwoFactorChallenge({
        verificationId: result.verificationId,
        method: result.method,
        detail: result.detail,
      });
    } else if (result.error) {
      failedToast("La connexion avec Google a échoué. Veuillez réessayer.");
    }
  };

  const tabs = [
    { title: "Se connecter", content: "login" },
    { title: "S'inscrire", content: "signup" },
  ];

  useEffect(() => {
    if (user) navigate(getRedirectPath(user.role || ""));
  }, [user, navigate]);

  return (
    <div className="flex flex-col md:flex-row items-center md:items-start justify-center min-h-screen bg-gray-50 p-6 md:py-12 gap-8">
      {/* Left Section */}
      <div className="hidden md:flex w-1/2 justify-center md:sticky md:top-12">
        <img
          src="/images/graph.png"
          alt="Graph Illustration"
          className="rounded-lg content-stretch shadow-lg mt-2 max-w-full w-[90%] "
        />
      </div>

      {/* Right Section */}
      <div className="w-full md:w-1/2 max-w-lg bg-white p-8 rounded-lg shadow-lg my-8 md:my-0">
        {/* Logo and Company Nam e */}
        <div className="flex items-center mb-8">
          <div className="w-full flex">
            <img
              src="/images/RAJAPI_logo.png"
              alt="RAJAPI Logo"
              className="h-12 w-12 mr-3"
            />
            <h1 className="text-2xl font-extrabold text-blue-500">
              RAJAPI-COP
            </h1>
          </div>
          {(signInLoading ||
            signUpLoading ||
            getProfileLoading ||
            verify2FALoading ||
            resend2FALoading ||
            resendVerificationLoading ||
            googleAuthLoading) && <SmallLoader modal={true} />}
        </div>

        <div className="mb-8 text-2xl font-bold">
          Bienvenue Sur Kiosk
          <div className="text-sm font-light mt-1">
            Content de vous Voir!! Connectez ou Inscrivez vous
          </div>
        </div>
        {needsVerification ? (
          <div className="space-y-6 mt-3 text-center">
            <h2 className="text-lg font-semibold">Vérifiez votre boîte mail</h2>

            {verificationEmail ? (
              <>
                <p className="text-sm text-gray-600">
                  Un email de vérification a été envoyé à{" "}
                  <span className="font-medium">{verificationEmail}</span>.
                  Cliquez sur le lien qu'il contient pour activer votre compte,
                  puis revenez vous connecter.
                </p>

                {pendingMentorRequest && (
                  <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-lg p-2">
                    Votre compte sera activé avec les permissions standards.
                    L'attribution du rôle Mentor sera disponible prochainement
                    et fera l'objet d'une validation séparée.
                  </p>
                )}

                {verificationResendSent ? (
                  <p className="text-sm text-green-600">
                    Un nouvel email a été envoyé.
                  </p>
                ) : (
                  <button
                    type="button"
                    onClick={onResendVerification}
                    disabled={resendVerificationLoading}
                    className="text-sm text-blue-500 hover:text-blue-700 disabled:opacity-60"
                  >
                    {resendVerificationLoading
                      ? "Envoi..."
                      : "Renvoyer l'email de vérification"}
                  </button>
                )}
              </>
            ) : (
              <>
                <p className="text-sm text-gray-600">
                  Votre compte n'a pas encore été vérifié. Entrez votre adresse
                  email pour recevoir un nouveau lien de vérification.
                </p>

                {verificationResendSent ? (
                  <p className="text-sm text-green-600">
                    Si un compte existe avec cette adresse, un email de
                    vérification vient de vous être envoyé.
                  </p>
                ) : (
                  <form
                    onSubmit={handleVerificationEmailSubmit(
                      onVerificationEmailSubmit,
                    )}
                    className="text-left"
                  >
                    <input
                      type="email"
                      placeholder="exemple@gmail.com"
                      {...registerVerificationEmail("email")}
                      className={`w-full p-2 border rounded-lg focus:outline-none focus:ring-2 ${
                        verificationEmailErrors.email
                          ? "border-red-500 focus:ring-red-500"
                          : "border-gray-300 focus:ring-blue-500"
                      }`}
                    />
                    {verificationEmailErrors.email && (
                      <p className="text-red-500 text-sm mt-1">
                        {verificationEmailErrors.email.message}
                      </p>
                    )}
                    <button
                      type="submit"
                      disabled={resendVerificationLoading}
                      className="w-full mt-3 bg-blue-500 hover:bg-blue-600 text-white py-2 rounded-lg disabled:opacity-60"
                    >
                      {resendVerificationLoading ? "Envoi..." : "Envoyer"}
                    </button>
                  </form>
                )}
              </>
            )}

            <button
              type="button"
              onClick={() => {
                setNeedsVerification(false);
                setVerificationEmail("");
                setVerificationResendSent(false);
                setPendingMentorRequest(false);
              }}
              className="w-full bg-blue-500 text-white py-2 rounded-lg hover:bg-blue-600"
            >
              Retour à la connexion
            </button>
          </div>
        ) : twoFactorChallenge ? (
          <div className="space-y-6 mt-3">
            <p className="text-sm text-gray-600">{twoFactorChallenge.detail}</p>
            <form
              onSubmit={handleTwoFactorSubmit(onTwoFactorSubmit)}
              className="space-y-6"
            >
              <div className="flex items-center">
                <FaLock className="text-gray-500 mr-3" />
                <div className="w-full">
                  <label
                    htmlFor="code"
                    className="block text-sm font-medium text-gray-700"
                  >
                    Code de vérification
                  </label>
                  <input
                    type="text"
                    id="code"
                    inputMode="numeric"
                    maxLength={6}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-blue-400"
                    placeholder="6 chiffres"
                    {...twoFactorRegister("code")}
                  />
                  {twoFactorErrors.code && (
                    <p className="text-red-500 text-xs mt-1">
                      {twoFactorErrors.code.message}
                    </p>
                  )}
                </div>
              </div>

              <button
                type="submit"
                className="w-full bg-blue-500 text-white py-2 rounded-lg hover:bg-blue-600"
              >
                Vérifier
              </button>

              <div className="flex justify-between text-sm">
                <button
                  type="button"
                  onClick={onResend2FA}
                  className="text-blue-500 hover:text-blue-700"
                >
                  Renvoyer le code
                </button>
                <button
                  type="button"
                  onClick={() => setTwoFactorChallenge(null)}
                  className="text-gray-500 hover:text-gray-700"
                >
                  Annuler
                </button>
              </div>
            </form>
          </div>
        ) : (
          <>
            {/* Tabs */}
            <div className="flex justify-center space-x-4 mb-6">
              {tabs.map((tab, index) => (
                <button
                  key={index}
                  className={`px-6 py-2 rounded-lg ${
                    activeTab === index
                      ? "bg-blue-500 text-white font-semibold"
                      : "bg-gray-200 text-gray-700 hover:bg-gray-300"
                  }`}
                  onClick={() => setActiveTab(index)}
                >
                  {tab.title}
                </button>
              ))}
            </div>

            {/* Form */}
            <div className="space-y-6 mt-3">
              {activeTab === 0 && (
                <form
                  onSubmit={handleLoginSubmit(onLoginSubmit)}
                  className="space-y-6 "
                >
                  <div className="flex items-center">
                    <FaUser className="text-gray-500 mr-3" aria-hidden="true" />
                    <div className="w-full">
                      <label
                        htmlFor="username"
                        className="block text-sm font-medium text-gray-700"
                      >
                        Nom d'utilisateur ou Email{" "}
                        <span className="text-red-500" aria-hidden="true">
                          *
                        </span>
                      </label>
                      <input
                        type="text"
                        id="username"
                        aria-required="true"
                        aria-invalid={!!loginErrors.username}
                        aria-describedby={
                          loginErrors.username ? "username-error" : undefined
                        }
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg  focus:ring-blue-400"
                        placeholder="Nom d'utilisateur ou email"
                        {...loginRegister("username")}
                      />
                      {loginErrors.username && (
                        <p
                          id="username-error"
                          className="text-red-500 text-xs mt-1"
                        >
                          {loginErrors.username.message}
                        </p>
                      )}
                    </div>
                  </div>

                  <div className="flex items-center">
                    <FaLock className="text-gray-500 mr-3" />
                    <div className="w-full relative">
                      <label
                        htmlFor="password"
                        className="block text-sm font-medium text-gray-700"
                      >
                        Mot de passe
                        <span className="text-red-500" aria-hidden="true">
                          *
                        </span>
                      </label>
                      <input
                        type={showLoginPassword ? "text" : "password"}
                        id="password"
                        className="w-full px-4 py-2 pr-10 border border-gray-300 rounded-lg focus:ring-blue-400"
                        aria-required="true"
                        aria-invalid={!!loginErrors.password}
                        aria-describedby={
                          loginErrors.password ? "password-error" : undefined
                        }
                        placeholder="Mot de passe"
                        {...loginRegister("password")}
                      />
                      <button
                        type="button"
                        onClick={() => setShowLoginPassword((v) => !v)}
                        className="absolute right-3 top-9 text-gray-400"
                        aria-label={
                          showLoginPassword
                            ? "Masquer le mot de passe"
                            : "Afficher le mot de passe"
                        }
                        aria-pressed={showLoginPassword}
                      >
                        {showLoginPassword ? (
                          <FaEyeSlash aria-hidden="true" />
                        ) : (
                          <FaEye aria-hidden="true" />
                        )}
                      </button>
                      {loginErrors.password && (
                        <p className="text-red-500 text-xs mt-1">
                          {loginErrors.password.message}
                        </p>
                      )}
                    </div>
                  </div>
                  <div className="-mt-10">
                    <Link
                      to="/login/resetPassword"
                      className="text-blue-500 text-sm hover:text-blue-700"
                    >
                      Mot de Passe Oublié ?
                    </Link>
                  </div>
                  <button
                    type="submit"
                    className="w-full bg-blue-500 text-white py-2 rounded-lg hover:bg-blue-600"
                  >
                    Se connecter
                  </button>
                </form>
              )}

              {activeTab === 1 && (
                <div className="mt-0">
                  <form
                    onSubmit={handleSignupSubmit(onSignupSubmit)}
                    className="space-y-6 overflow-y-auto"
                  >
                    <div className="flex items-center">
                      <FaUser
                        className="text-gray-500 mr-3"
                        aria-hidden="true"
                      />
                      <div className="w-full">
                        <label
                          htmlFor="username"
                          className="block text-sm font-medium text-gray-700"
                        >
                          Nom d'utilisateur
                          <span className="text-red-500" aria-hidden="true">
                            *
                          </span>
                        </label>
                        <input
                          type="text"
                          id="username"
                          className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-400"
                          aria-required="true"
                          aria-invalid={!!signupErrors.username}
                          aria-describedby={
                            signupErrors.username ? "username-error" : undefined
                          }
                          placeholder="Nom d'utilisateur"
                          {...signupRegister("username")}
                        />
                        {signupErrors.username && (
                          <p className="text-red-500 text-xs mt-1">
                            {signupErrors.username.message}
                          </p>
                        )}
                      </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div className="flex items-center">
                        <FaUser
                          className="text-gray-500 mr-3"
                          aria-hidden="true"
                        />
                        <div className="w-full">
                          <label
                            htmlFor="first_name"
                            className="block text-sm font-medium text-gray-700"
                          >
                            Prénom
                            <span className="text-red-500" aria-hidden="true">
                              *
                            </span>
                          </label>
                          <input
                            type="text"
                            id="first_name"
                            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-400"
                            aria-required="true"
                            aria-invalid={!!signupErrors.first_name}
                            aria-describedby={
                              signupErrors.first_name
                                ? "first_name-error"
                                : undefined
                            }
                            placeholder="Prénom"
                            {...signupRegister("first_name")}
                          />
                          {signupErrors.first_name && (
                            <p className="text-red-500 text-xs mt-1">
                              {signupErrors.first_name.message}
                            </p>
                          )}
                        </div>
                      </div>

                      <div className="flex items-center">
                        <FaUser
                          className="text-gray-500 mr-3"
                          aria-hidden="true"
                        />
                        <div className="w-full">
                          <label
                            htmlFor="last_name"
                            className="block text-sm font-medium text-gray-700"
                          >
                            Nom
                            <span className="text-red-500" aria-hidden="true">
                              *
                            </span>
                          </label>
                          <input
                            type="text"
                            id="last_name"
                            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-400"
                            aria-required="true"
                            aria-invalid={!!signupErrors.last_name}
                            aria-describedby={
                              signupErrors.last_name
                                ? "last_name-error"
                                : undefined
                            }
                            placeholder="Nom"
                            {...signupRegister("last_name")}
                          />
                          {signupErrors.last_name && (
                            <p className="text-red-500 text-xs mt-1">
                              {signupErrors.last_name.message}
                            </p>
                          )}
                        </div>
                      </div>
                    </div>

                    <div className="flex items-center">
                      <FaEnvelope
                        className="text-gray-500 mr-3"
                        aria-hidden="true"
                      />
                      <div className="w-full">
                        <label
                          htmlFor="email"
                          className="block text-sm font-medium text-gray-700"
                        >
                          Email
                          <span className="text-red-500" aria-hidden="true">
                            *
                          </span>
                        </label>
                        <input
                          type="email"
                          id="email"
                          className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-400"
                          aria-required="true"
                          aria-invalid={!!signupErrors.email}
                          aria-describedby={
                            signupErrors.email ? "email-error" : undefined
                          }
                          placeholder="Email"
                          {...signupRegister("email")}
                        />
                        {signupErrors.email && (
                          <p className="text-red-500 text-xs mt-1">
                            {signupErrors.email.message}
                          </p>
                        )}
                      </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div className="flex items-center">
                        <FaUser
                          className="text-gray-500 mr-3"
                          aria-hidden="true"
                        />
                        <div className="w-full">
                          <label
                            htmlFor="phone_number"
                            className="block text-sm font-medium text-gray-700"
                          >
                            Téléphone
                            <span className="text-red-500" aria-hidden="true">
                              *
                            </span>
                          </label>
                          <input
                            type="tel"
                            id="phone_number"
                            maxLength={13}
                            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-400"
                            aria-required="true"
                            aria-invalid={!!signupErrors.phone_number}
                            aria-describedby={
                              signupErrors.phone_number
                                ? "phone_number-error"
                                : undefined
                            }
                            placeholder="+237..."
                            {...signupRegister("phone_number", {
                              onChange: (e) => {
                                let value = e.target.value;
                                if (!value.startsWith("+237")) {
                                  value = "+237" + value.replace(/\D/g, "");
                                }
                                e.target.value = value;
                              },
                            })}
                          />

                          {signupErrors.phone_number && (
                            <p className="text-red-500 text-xs mt-1">
                              {signupErrors.phone_number.message}
                            </p>
                          )}
                        </div>
                      </div>

                      <div className="flex items-center">
                        <FaUser
                          className="text-gray-500 mr-3"
                          aria-hidden="true"
                        />
                        <div className="w-full">
                          <label
                            htmlFor="country"
                            className="block text-sm font-medium text-gray-700"
                          >
                            Pays
                            <span className="text-red-500" aria-hidden="true">
                              *
                            </span>
                          </label>
                          <select
                            id="country"
                            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-400"
                            aria-required="true"
                            aria-invalid={!!signupErrors.country}
                            aria-describedby={
                              signupErrors.country ? "country-error" : undefined
                            }
                            defaultValue=""
                            {...signupRegister("country")}
                          >
                            <option value="" disabled>
                              Sélectionnez votre pays
                            </option>
                            {COUNTRIES.map((c) => (
                              <option key={c} value={c}>
                                {c}
                              </option>
                            ))}
                          </select>
                          {signupErrors.country && (
                            <p className="text-red-500 text-xs mt-1">
                              {signupErrors.country.message}
                            </p>
                          )}
                        </div>
                      </div>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Type de profil
                        <span className="text-red-500" aria-hidden="true">
                          *
                        </span>
                      </label>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                        <label
                          className={`flex items-start gap-2 border rounded-lg p-3 cursor-pointer transition ${
                            selectedRole === "jeuneInnovateur"
                              ? "border-blue-500 bg-blue-50"
                              : "border-gray-300"
                          }`}
                        >
                          <input
                            type="radio"
                            value="jeuneInnovateur"
                            className="mt-1"
                            {...signupRegister("role")}
                          />
                          <span>
                            <span className="block text-sm font-medium text-gray-800">
                              Jeune innovateur / Porteur de projet
                            </span>
                            <span className="block text-xs text-gray-500">
                              Soumettez et suivez vos projets, accédez au
                              mentorat, aux formations et à la communauté.
                            </span>
                          </span>
                        </label>

                        <label
                          className={`flex items-start gap-2 border rounded-lg p-3 cursor-pointer transition ${
                            selectedRole === "mentor"
                              ? "border-blue-500 bg-blue-50"
                              : "border-gray-300"
                          }`}
                        >
                          <input
                            type="radio"
                            value="mentor"
                            className="mt-1"
                            {...signupRegister("role")}
                          />
                          <span>
                            <span className="block text-sm font-medium text-gray-800">
                              Mentor
                            </span>
                            <span className="block text-xs text-gray-500">
                              Accompagnez des porteurs de projets. L'activation
                              du rôle Mentor est soumise à validation par un
                              administrateur.
                            </span>
                          </span>
                        </label>
                      </div>
                      {signupErrors.role && (
                        <p className="text-red-500 text-xs mt-1">
                          {signupErrors.role.message}
                        </p>
                      )}
                    </div>

                    <div className="flex items-center">
                      <FaLock className="text-gray-500 mr-3" />
                      <div className="w-full relative">
                        <label
                          htmlFor="signup_password"
                          className="block text-sm font-medium text-gray-700"
                        >
                          Mot de passe
                          <span className="text-red-500" aria-hidden="true">
                            *
                          </span>
                        </label>
                        <input
                          type={showSignupPassword ? "text" : "password"}
                          id="signup_password"
                          className="w-full px-4 py-2 pr-10 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-400"
                          placeholder="Mot de passe"
                          aria-required="true"
                          aria-invalid={!!signupErrors.password}
                          aria-describedby={
                            signupErrors.password
                              ? "signup_password-error"
                              : undefined
                          }
                          {...signupRegister("password")}
                        />
                        <button
                          type="button"
                          onClick={() => setShowSignupPassword((v) => !v)}
                          className="absolute right-3 top-9 text-gray-400"
                          aria-label={
                            showSignupPassword
                              ? "Masquer le mot de passe"
                              : "Afficher le mot de passe"
                          }
                          aria-pressed={showSignupPassword}
                        >
                          {showSignupPassword ? (
                            <FaEyeSlash aria-hidden="true" />
                          ) : (
                            <FaEye aria-hidden="true" />
                          )}
                        </button>

                        {signupPasswordValue && (
                          <div className="mt-2">
                            <div className="h-1.5 w-full bg-gray-200 rounded-full overflow-hidden">
                              <div
                                className={`h-full transition-all duration-200 ${passwordStrength.color}`}
                                style={{
                                  width: `${passwordStrength.widthPercent}%`,
                                }}
                              />
                            </div>
                            <p
                              className={`text-xs mt-1 ${
                                passwordStrength.level === "Faible"
                                  ? "text-red-600"
                                  : passwordStrength.level === "Moyen"
                                    ? "text-yellow-600"
                                    : "text-green-600"
                              }`}
                            >
                              Sécurité du mot de passe :{" "}
                              {passwordStrength.level}
                            </p>
                          </div>
                        )}

                        {signupErrors.password && (
                          <p className="text-red-500 text-xs mt-1">
                            {signupErrors.password.message}
                          </p>
                        )}
                      </div>
                    </div>

                    <div className="flex items-center">
                      <FaLock className="text-gray-500 mr-3" />
                      <div className="w-full relative">
                        <label
                          htmlFor="confirm_password"
                          className="block text-sm font-medium text-gray-700"
                        >
                          Confirmez le mot de passe
                          <span className="text-red-500" aria-hidden="true">
                            *
                          </span>
                        </label>
                        <input
                          type={showSignupConfirmPassword ? "text" : "password"}
                          id="confirm_password"
                          className="w-full px-4 py-2 pr-10 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-400"
                          aria-required="true"
                          aria-invalid={!!signupErrors.confirm_password}
                          aria-describedby={
                            signupErrors.confirm_password
                              ? "confirm_password-error"
                              : undefined
                          }
                          placeholder="Mot de passe"
                          {...signupRegister("confirm_password")}
                        />
                        <button
                          type="button"
                          onClick={() =>
                            setShowSignupConfirmPassword((v) => !v)
                          }
                          className="absolute right-3 top-9 text-gray-400"
                          aria-label={
                            showSignupConfirmPassword
                              ? "Masquer le mot de passe"
                              : "Afficher le mot de passe"
                          }
                          aria-pressed={showSignupConfirmPassword}
                        >
                          {showSignupConfirmPassword ? (
                            <FaEyeSlash aria-hidden="true" />
                          ) : (
                            <FaEye aria-hidden="true" />
                          )}
                        </button>
                        {signupErrors.confirm_password && (
                          <p className="text-red-500 text-xs mt-1">
                            {signupErrors.confirm_password.message}
                          </p>
                        )}
                      </div>
                    </div>

                    <div className="flex items-center ml-8">
                      <input
                        type="checkbox"
                        id="newsletter_subscription"
                        className="h-4 w-4 text-blue-500 focus:ring-blue-400 border-gray-300 rounded"
                        {...signupRegister("newsletter_subscription")}
                      />
                      <label
                        htmlFor="newsletter_subscription"
                        className="ml-2 block text-sm text-gray-700"
                      >
                        S'abonner à la newsletter
                      </label>
                    </div>

                    <div className="flex items-start ml-8">
                      <input
                        type="checkbox"
                        id="terms_accepted"
                        className="h-4 w-4 mt-0.5 text-blue-500 focus:ring-blue-400 border-gray-300 rounded"
                        {...signupRegister("terms_accepted")}
                      />
                      <label
                        htmlFor="terms_accepted"
                        className="ml-2 block text-sm text-gray-700"
                      >
                        J'ai lu et j'accepte les{" "}
                        <a
                          href="/conditions-utilisation"
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-blue-500 hover:underline"
                        >
                          Conditions Générales d'Utilisation
                        </a>{" "}
                        et la{" "}
                        <a
                          href="/politique-confidentialite"
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-blue-500 hover:underline"
                        >
                          Politique de confidentialité
                        </a>{" "}
                        de RAJAPI-COP.
                      </label>
                    </div>
                    {signupErrors.terms_accepted && (
                      <p className="text-red-500 text-xs -mt-4 ml-8">
                        {signupErrors.terms_accepted.message}
                      </p>
                    )}

                    <button
                      type="submit"
                      disabled={!termsAccepted}
                      className="w-full bg-blue-500 text-white py-2 rounded-lg hover:bg-blue-600 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:bg-blue-500"
                    >
                      S'inscrire
                    </button>
                  </form>
                </div>
              )}
            </div>
          </>
        )}

        <div className="relative flex items-center my-6">
          <div className="flex-grow border-t border-gray-300"></div>
          <span className="px-4 text-gray-500">Ou</span>
          <div className="flex-grow border-t border-gray-300"></div>
        </div>

        <div className="flex justify-center">
          <GoogleSignInButton
            onCredential={onGoogleCredential}
            disabled={googleAuthLoading}
          />
        </div>

        <div className="mt-4">
          <SupportModal />
        </div>
      </div>
    </div>
  );
};

export default LoginSignupTab;
